import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  createAndStoreReturnUrl,
  reduceAllowedProviders,
  getQueryParams,
  sanitizeReturnUrl,
} from 'platform/user/authentication/utilities';
import { useFeatureToggle } from 'platform/utilities/feature-toggles';
import { AUTHN_SETTINGS, AUTH_SSO_APPS, OCC_CLIENT_IDS } from '../constants';
import { externalApplicationsConfig } from '../usip-config';
import {
  authenticateWithPasskey,
  getPasskeyErrorMessage,
  reportPasskeyAuthenticationStatus,
  storePasskeyOriginClient,
} from '../passkeys';
import LoginButton from './LoginButton';
import SmartCardLoginButton from './SmartCardLoginButton';
import { SignInButton } from './DesignSystemSignInAndVerifyButtons';
import PasskeyButton from './PasskeyButton';
import { PasskeyAlert } from './PasskeyAlertSlot';

const getNextParam = () => {
  const nextParam = new URLSearchParams(window.location.search).get('next');
  return nextParam && nextParam !== 'loginModal' ? nextParam : null;
};

const storeReturnUrlForCallback = () => {
  const nextPath = sanitizeReturnUrl(getNextParam());

  if (nextPath) {
    sessionStorage.setItem(
      AUTHN_SETTINGS.RETURN_URL,
      `${window.location.origin}${nextPath}`,
    );
  } else {
    createAndStoreReturnUrl();
  }
};

export default function LoginActions({
  clearPreviewEnabled,
  externalApplication,
  isUnifiedSignIn,
  showPasskeyButton,
}) {
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const [passkeyError, setPasskeyError] = useState('');
  const [passkeyErrorKey, setPasskeyErrorKey] = useState(0);
  const passkeyStateRef = useRef(null);
  const [useOAuth, setOAuth] = useState();
  const {
    OAuth,
    application,
    clientId,
    codeChallenge,
    state,
    scope,
    nonce,
    authorizeSsoId,
    prompt,
  } = getQueryParams();

  const { OAuthEnabled, allowedSignInProviders } =
    externalApplicationsConfig[externalApplication] ??
    externalApplicationsConfig.default;

  const actionLocation = isUnifiedSignIn ? 'usip' : 'modal';

  const { TOGGLE_NAMES, useToggleValue } = useFeatureToggle();
  const clearButtonOrder = useToggleValue(TOGGLE_NAMES.clearButtonOrder);
  const clearSigninEnabled = useToggleValue(TOGGLE_NAMES.clearSigninEnabled);
  const pivSigninEnabled = useToggleValue(TOGGLE_NAMES.pivSigninEnabled);
  const identityPasskeyEnabled = useToggleValue(
    TOGGLE_NAMES.identityPasskeyEnabled,
  );
  const occPasskeyEnabled = useToggleValue(TOGGLE_NAMES.occPasskeyEnabled);
  const passkeyEnabled = identityPasskeyEnabled || showPasskeyButton;
  const arpPivEnabled = pivSigninEnabled && application === 'arp';
  const designSystemSignInButtonsEnabled = useToggleValue(
    TOGGLE_NAMES.designSystemSignInButtonsEnabled,
  );

  useEffect(() => {
    setOAuth(OAuthEnabled && OAuth === 'true');
  }, [OAuth, OAuthEnabled]);

  const handlePasskeySignIn = async () => {
    setIsPasskeyLoading(true);
    setPasskeyError('');
    passkeyStateRef.current = null;

    let pagehideHandler = null;

    const onOptionsSuccess = signedState => {
      pagehideHandler = () =>
        reportPasskeyAuthenticationStatus(signedState, 'abandoned');
      window.addEventListener('pagehide', pagehideHandler);
    };

    try {
      const { redirectUrl } = await authenticateWithPasskey({
        application,
        clientId,
        queryParams: {
          codeChallenge,
          state,
          scope,
          ...(authorizeSsoId && { authorizeSsoId }),
        },
        onOptionsSuccess,
      });

      storeReturnUrlForCallback();
      window.location.replace(redirectUrl);
    } catch (error) {
      if (error.signedState) {
        passkeyStateRef.current = error.signedState;
      }
      setPasskeyError(getPasskeyErrorMessage(error));
      setPasskeyErrorKey(k => k + 1);
    } finally {
      setIsPasskeyLoading(false);
      if (pagehideHandler) {
        window.removeEventListener('pagehide', pagehideHandler);
      }
    }
  };

  const LoginButtonType = designSystemSignInButtonsEnabled
    ? SignInButton
    : LoginButton;

  const PasskeyButtonType = designSystemSignInButtonsEnabled
    ? SignInButton
    : PasskeyButton;

  const { pathname } = window.location;
  const currentPath =
    !pathname || pathname === '/' || pathname.startsWith('/sign-in')
      ? null
      : pathname;
  const passkeyReturnPath = getNextParam() || currentPath;
  const createPasskeyHref = passkeyReturnPath
    ? `/sign-in/create-a-passkey?next=${encodeURIComponent(passkeyReturnPath)}`
    : '/sign-in/create-a-passkey';

  const handleCreatePasskeyClick = () =>
    storePasskeyOriginClient({
      application,
      clientId,
      codeChallenge,
      state,
      scope,
      authorizeSsoId,
    });

  const handleCreatePasskeyFromError = () => {
    if (passkeyStateRef.current) {
      reportPasskeyAuthenticationStatus(
        passkeyStateRef.current,
        'create_from_error',
      );
      passkeyStateRef.current = null;
    }
    storePasskeyOriginClient({
      application,
      clientId,
      codeChallenge,
      state,
      scope,
      authorizeSsoId,
    });
  };

  return (
    <div className="row">
      <div className="columns print-full-width sign-in-wrapper">
        {passkeyError && (
          <PasskeyAlert
            key={passkeyErrorKey}
            status="info"
            headline="Sign in to your account to create a passkey"
          >
            <p className="vads-u-margin-y--0">
              You need to sign in with your <strong>CLEAR</strong>,{' '}
              <strong>ID.me</strong>, or <strong>Login.gov</strong> account to
              create a passkey. Then you can sign in with your passkey.
            </p>
            <va-link-action
              type="secondary"
              href={createPasskeyHref}
              text="Create passkey for VA.gov"
              onClick={handleCreatePasskeyFromError}
            />
          </PasskeyAlert>
        )}

        {pivSigninEnabled && application === 'arp' && (
          <SmartCardLoginButton actionLocation={actionLocation} />
        )}

        {(() => {
          const providers = reduceAllowedProviders(
            allowedSignInProviders,
          ).filter(
            csp =>
              csp !== 'clear' ||
              ((clearSigninEnabled || clearPreviewEnabled) &&
                application !== 'arp'),
          );
          if (clearButtonOrder) {
            providers.sort((a, b) => {
              if (a === 'clear') return -1;
              if (b === 'clear') return 1;
              return 0;
            });
          }
          return providers;
        })().map(csp => (
          <LoginButtonType
            key={csp}
            csp={csp}
            useOAuth={useOAuth}
            actionLocation={actionLocation}
            queryParams={{
              clientId,
              codeChallenge,
              state,
              nonce,
              ...(isUnifiedSignIn && authorizeSsoId && { authorizeSsoId }),
              ...(prompt && { prompt }),
            }}
          />
        ))}

        {passkeyEnabled && !arpPivEnabled && (
          <va-link-action
            type="secondary"
            href="/resources/creating-an-account-for-vagov/"
            text="Create an account"
          />
        )}

        {passkeyEnabled &&
          (!AUTH_SSO_APPS.includes(clientId) ||
            (occPasskeyEnabled && OCC_CLIENT_IDS.includes(clientId))) &&
          application !== 'arp' && (
            <>
              <span className="sr-only" role="status" aria-live="polite">
                {isPasskeyLoading ? 'Signing in with passkey...' : ''}
              </span>
              <PasskeyButtonType
                csp="passkey"
                type="button"
                className="usa-button usa-button--outline vads-u-margin-y--1p5 vads-u-padding-y--2 passkey-signin-button"
                data-dd-action-name={`passkey-signin-button-${actionLocation}`}
                onClick={handlePasskeySignIn}
                disabled={isPasskeyLoading}
                isPasskeyLoading={isPasskeyLoading}
              />
              <va-link-action
                type="secondary"
                href={createPasskeyHref}
                text="Create passkey for VA.gov"
                onClick={handleCreatePasskeyClick}
              />
            </>
          )}

        {(!passkeyEnabled && !clearSigninEnabled) || arpPivEnabled ? (
          <a href="https://www.va.gov/resources/creating-an-account-for-vagov">
            Learn about creating an ID.me or Login.gov account
          </a>
        ) : null}
      </div>
    </div>
  );
}

LoginActions.propTypes = {
  clearPreviewEnabled: PropTypes.bool,
  externalApplication: PropTypes.string,
  isUnifiedSignIn: PropTypes.bool,
  showPasskeyButton: PropTypes.bool,
};
